export type EmbedInfo = {
  url: string;
  /** CSS aspect-ratio (e.g. "16 / 9"). Ignored when fixedHeight is set. */
  aspect?: string;
  /** Use a fixed pixel height instead of an aspect ratio (e.g. SoundCloud audio). */
  fixedHeight?: number;
  /** Optional max width (px) — used for portrait media like YouTube Shorts. */
  maxWidth?: number;
  /** Iframe title for accessibility. */
  title: string;
};

/** Safely build URL, even if protocol is missing. */
export function toURL(raw: string): URL | null {
  try {
    return new URL(raw);
  } catch {
    try {
      return new URL(`https://${raw}`);
    } catch {
      return null;
    }
  }
}

/**
 * Returns embed info for supported 3rd-party media URLs, or null.
 * Supports: YouTube (youtube.com/watch, m.youtube.com/watch, youtu.be)
 *           Vimeo (vimeo.com/ID)
 *           Nest (video.nest.com/clip/)
 *           Dropcam (clips.dropcam.com/)
 *           Ring (ring.com/share/, share.ring.com/)
 *           SoundCloud (soundcloud.com/...)
 */
export function getEmbedInfo(raw: string): EmbedInfo | null {
  const u = toURL(raw);
  if (!u) return null;
  const host = u.hostname.toLowerCase();

  if (
    host === 'www.youtube.com' ||
    host === 'm.youtube.com' ||
    host === 'youtube.com'
  ) {
    // Shorts: https://www.youtube.com/shorts/{id}
    const shortsMatch = u.pathname.match(/^\/shorts\/([\w-]{6,})/);
    if (shortsMatch) {
      return {
        url: `https://www.youtube.com/embed/${shortsMatch[1]}`,
        aspect: '9 / 16',
        maxWidth: 360,
        title: 'YouTube Short',
      };
    }
    // Embed url already (rare but harmless): /embed/{id}
    const embedMatch = u.pathname.match(/^\/embed\/([\w-]{6,})/);
    if (embedMatch) {
      return {
        url: `https://www.youtube.com/embed/${embedMatch[1]}`,
        aspect: '16 / 9',
        title: 'YouTube video',
      };
    }
    if (u.pathname !== '/watch') return null;
    const id = u.searchParams.get('v');
    if (!id) return null;
    return {
      url: `https://www.youtube.com/embed/${id}`,
      aspect: '16 / 9',
      title: 'YouTube video',
    };
  }
  if (host === 'youtu.be') {
    const id = u.pathname.replace(/^\//, '').split('/')[0];
    if (!id) return null;
    return {
      url: `https://www.youtube.com/embed/${id}`,
      aspect: '16 / 9',
      title: 'YouTube video',
    };
  }

  if (host === 'vimeo.com' || host === 'www.vimeo.com') {
    const id = u.pathname.replace(/^\//, '').split('/')[0];
    if (!id || !/^\d+$/.test(id)) return null;
    return {
      url: `https://player.vimeo.com/video/${id}`,
      aspect: '16 / 9',
      title: 'Vimeo video',
    };
  }

  if (host === 'video.nest.com' && u.pathname.startsWith('/clip/')) {
    return { url: u.href, aspect: '16 / 9', title: 'Nest clip' };
  }

  if (host === 'clips.dropcam.com') {
    return { url: u.href, aspect: '16 / 9', title: 'Dropcam clip' };
  }

  if (
    (host === 'ring.com' || host === 'www.ring.com') &&
    u.pathname.startsWith('/share/')
  ) {
    return { url: u.href, aspect: '16 / 9', title: 'Ring clip' };
  }
  if (host === 'share.ring.com') {
    return { url: u.href, aspect: '16 / 9', title: 'Ring clip' };
  }

  if (host === 'soundcloud.com' || host === 'www.soundcloud.com') {
    return {
      url: `https://w.soundcloud.com/player/?url=${encodeURIComponent(
        u.href
      )}&color=%23948780&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false`,
      fixedHeight: 166,
      title: 'SoundCloud audio',
    };
  }

  return null;
}

/** Extract candidate URLs from an HTML/text blob (href attrs + raw http(s) links). */
export function extractUrls(html: string): string[] {
  if (!html) return [];
  const found = new Set<string>();
  const hrefRegex = /href=["']([^"']+)["']/gi;
  const rawRegex = /https?:\/\/[^\s<>"')]+/gi;
  let m: RegExpExecArray | null;
  while ((m = hrefRegex.exec(html))) found.add(m[1]);
  while ((m = rawRegex.exec(html))) found.add(m[0]);
  return Array.from(found);
}
